import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle, Building2, Shield, Clock, Users, Award, TrendingUp, FileText, Scale, Briefcase } from 'lucide-react';

const Home = () => {
  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "NextGen Filling Association",
    "description": "Expert CA services, company registration, GST, trademark, and compliance solutions for entrepreneurs and businesses across India",
    "url": "https://nextgenfilling.com",
    "telephone": ["+91-9509363261", "+91-9541709908"],
    "email": "info@nextgenfilling.com",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "IN"
    },
    "areaServed": "IN",
    "priceRange": "₹₹",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "150"
    }
  };
  
  const services = [
    {
      icon: Building2,
      title: 'Company Registration',
      description: 'Register your Private Limited, LLP, OPC, or other business entities with expert guidance.',
      link: '/services/private-limited',
    },
    {
      icon: FileText,
      title: 'GST & Tax Services',
      description: 'Complete GST registration, filing, and compliance services for your business.',
      link: '/services/gst',
    },
    {
      icon: Scale,
      title: 'Trademark & IP',
      description: 'Protect your brand with trademark registration and intellectual property services.',
      link: '/services/trademark',
    },
    {
      icon: Shield,
      title: 'Compliance Services',
      description: 'Stay compliant with all regulatory requirements and avoid penalties.',
      link: '/services/pvt-ltd-compliance',
    },
    {
      icon: Award,
      title: 'Licenses & Certifications',
      description: 'Get FSSAI, IEC, MSME, and other essential business licenses quickly.',
      link: '/services/fssai',
    },
    {
      icon: Briefcase,
      title: 'Business Solutions',
      description: 'Director changes, address updates, capital increase, and more services.',
      link: '/services/director-change',
    },
  ];

  const features = [
    {
      icon: Users,
      title: 'Expert CA Team',
      description: 'Experienced chartered accountants with 10+ years in the industry',
    },
    {
      icon: Clock,
      title: 'Fast Processing',
      description: 'Quick turnaround time with efficient documentation and filing',
    },
    {
      icon: Shield,
      title: 'Secure & Confidential',
      description: 'Your data is protected with highest security standards',
    },
    {
      icon: TrendingUp,
      title: 'Affordable Pricing',
      description: 'Transparent and competitive pricing for all services',
    },
  ];

  const testimonials = [
    {
      name: 'Rahul Sharma',
      company: 'Tech Innovations Pvt Ltd',
      text: 'NextGen Filling helped us set up our company quickly and efficiently. Their team is professional and knowledgeable.',
      rating: 5,
    },
    {
      name: 'Priya Patel',
      company: 'Green Foods LLP',
      text: 'Excellent service for GST registration and compliance. They made the entire process hassle-free.',
      rating: 5,
    },
    {
      name: 'Amit Kumar',
      company: 'Digital Solutions',
      text: 'Professional team with great expertise. Highly recommend for trademark and business registration services.',
      rating: 5,
    },
  ];

  const process = [
    { step: '1', title: 'Consultation', description: 'Share your requirements with our experts' },
    { step: '2', title: 'Documentation', description: 'We help you prepare all necessary documents' },
    { step: '3', title: 'Filing', description: 'Submit applications to relevant authorities' },
    { step: '4', title: 'Completion', description: 'Receive your certificates and registrations' },
  ];

  return (
    <>
      <Helmet>
        <title>NextGen Filling Association | CA Services, Company Registration & Business Compliance</title>
        <meta name="description" content="Expert CA firm in India offering company registration (Pvt Ltd, LLP, OPC), GST registration, trademark, compliance services, and business licenses. Trusted by 5000+ clients." />
        <meta name="keywords" content="company registration in india, private limited company registration, llp registration india, opc registration, gst registration services, trademark registration india, ca firm in india, business compliance services, fssai license, startup india registration" />
        <link rel="canonical" href="https://nextgenfilling.com" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      
      <div className="min-h-screen" data-testid="home-page">
      <section
        className="relative h-[600px] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(15, 23, 42, 0.9), rgba(15, 23, 42, 0.7)), url('https://images.unsplash.com/photo-1768302368425-48328f7fb90d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBmaW5hbmNlJTIwc2t5c2NyYXBlciUyMGJsdWUlMjBnb2xkfGVufDB8fHx8MTc3MjI1MDUxN3ww&ixlib=rb-4.1.0&q=85')`,
        }}
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Your Trusted Partner for
            <span className="bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent"> Business Success</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-2xl mx-auto" style={{ fontFamily: 'Inter, sans-serif' }}>
            Expert CA services, company registration, GST, trademark, and compliance solutions for entrepreneurs and businesses across India.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button
                className="bg-amber-500 text-white hover:bg-amber-600 rounded-lg px-8 py-6 text-lg font-bold shadow-lg shadow-amber-500/30 transition-all hover:-translate-y-1"
                data-testid="hero-cta-button"
              >
                Get Started Today
              </Button>
            </Link>
            <a href="tel:+919509363261">
              <Button
                className="bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 rounded-lg px-8 py-6 text-lg font-medium transition-all"
                data-testid="hero-call-button"
              >
                Call +91 9509363261
              </Button>
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Our Services
            </h2>
            <p className="text-base text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>
              Comprehensive business solutions tailored to your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Link
                  key={index}
                  to={service.link}
                  className="group relative overflow-hidden bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                  data-testid={`service-card-${index}`}
                >
                  <div className="flex items-center justify-center w-16 h-16 bg-amber-100 rounded-lg mb-4">
                    <Icon className="text-amber-600" size={32} />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {service.title}
                  </h3>
                  <p className="text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {service.description}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Why Choose Us
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl border border-slate-200 hover:border-amber-400/50 transition-colors text-center"
                  data-testid={`feature-card-${index}`}
                >
                  <div className="flex items-center justify-center w-12 h-12 bg-slate-100 rounded-full mx-auto mb-4">
                    <Icon className="text-blue-800" size={24} />
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {feature.title}
                  </h3>
                  <p className="text-sm text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Our Process
            </h2>
            <p className="text-base text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>
              Simple and streamlined steps to get your business started
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="text-center" data-testid={`process-step-${index}`}>
                <div className="flex items-center justify-center w-16 h-16 bg-blue-800 text-white rounded-full mx-auto mb-4 text-2xl font-bold">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {item.title}
                </h3>
                <p className="text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Client Testimonials
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg border-l-4 border-amber-500"
                data-testid={`testimonial-card-${index}`}
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg
                      key={i}
                      className="w-5 h-5 text-amber-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-slate-600 mb-4 italic" style={{ fontFamily: 'Inter, sans-serif' }}>
                  "{testimonial.text}"
                </p>
                <div>
                  <p className="font-semibold text-slate-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {testimonial.name}
                  </p>
                  <p className="text-sm text-slate-500">{testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Ready to Start Your Business Journey?
          </h2>
          <p className="text-lg text-slate-300 mb-8" style={{ fontFamily: 'Inter, sans-serif' }}>
            Get expert guidance and support for all your business registration and compliance needs.
          </p>
          <Link to="/contact">
            <Button
              className="bg-amber-500 text-white hover:bg-amber-600 rounded-lg px-8 py-6 text-lg font-bold shadow-lg shadow-amber-500/30 transition-all hover:-translate-y-1"
              data-testid="cta-contact-button"
            >
              Contact Us Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
    </>
  );
};

export default Home;